/* oxlint-disable typescript/no-non-null-assertion, oxc/no-map-spread, typescript/no-unnecessary-type-assertion -- bounded collapse mutates guaranteed retained bin; mapped discriminant keeps rebuild explicit */
import type { QuantileSummary } from './tel-metrics-types'

/**
 * DDSketch - A quantile sketch with relative-error guarantees.
 *
 * Based on Datadog's DDSketch algorithm.
 * Original: https://github.com/DataDog/sketches-java
 * License: Apache 2.0
 */

// ============================================================================
// Store - Maps bucket indices to counts
// ============================================================================

export interface Bin {
  index: number
  count: number
}

const DEFAULT_GROWTH_INCREMENT = 64

/**
 * Dense array-based store with dynamic resizing.
 * O(1) add, unbounded memory growth.
 */
export class DenseStore {
  private counts: number[] = []
  private offset = 0
  private _minIndex = Number.MAX_SAFE_INTEGER
  private _maxIndex = Number.MIN_SAFE_INTEGER
  private readonly growthIncrement: number

  constructor(growthIncrement = DEFAULT_GROWTH_INCREMENT) {
    this.growthIncrement = growthIncrement
  }

  private static copyFrom(store: DenseStore): DenseStore {
    const copy = new DenseStore(store.growthIncrement)
    copy._minIndex = store._minIndex
    copy._maxIndex = store._maxIndex
    copy.offset = store.offset
    if (store.counts.length > 0 && !store.isEmpty()) {
      const start = store._minIndex - store.offset
      const end = store._maxIndex - store.offset + 1
      copy.counts = store.counts.slice(start, end)
      copy.offset = store._minIndex
    }
    return copy
  }

  add(index: number, count = 1): void {
    if (count < 0) throw new Error('Count cannot be negative')
    if (count === 0) return

    const arrayIndex = this.normalize(index)
    this.counts[arrayIndex] += count
  }

  private normalize(index: number): number {
    if (index < this._minIndex || index > this._maxIndex) {
      this.extendRange(index, index)
    }
    return index - this.offset
  }

  private extendRange(newMin: number, newMax: number): void {
    newMin = Math.min(newMin, this._minIndex)
    newMax = Math.max(newMax, this._maxIndex)

    if (this.isEmpty()) {
      const length = this.getNewLength(newMin, newMax)
      if (this.counts.length === 0 || length > this.counts.length) {
        this.counts = Array.from({ length }, () => 0)
      }
      this.offset = newMin
      this._minIndex = newMin
      this._maxIndex = newMax
      this.centerCounts(newMin, newMax)
    } else if (
      newMin >= this.offset &&
      newMax < this.offset + this.counts.length
    ) {
      this._minIndex = newMin
      this._maxIndex = newMax
    } else {
      const newLength = this.getNewLength(newMin, newMax)
      if (newLength > this.counts.length) {
        const newCounts = Array.from({ length: newLength }, () => 0)
        const oldStart = this._minIndex - this.offset
        const oldEnd = this._maxIndex - this.offset + 1
        for (let i = oldStart; i < oldEnd; i++) {
          newCounts[i] = this.counts[i]
        }
        this.counts = newCounts
      }
      this.centerCounts(newMin, newMax)
    }
  }

  private getNewLength(newMin: number, newMax: number): number {
    const desired = newMax - newMin + 1
    const overhead = Math.floor(this.growthIncrement * 0.1)
    return (
      Math.ceil((desired + overhead) / this.growthIncrement) *
      this.growthIncrement
    )
  }

  private centerCounts(newMin: number, newMax: number): void {
    const middleIndex = newMin + Math.floor((newMax - newMin + 1) / 2)
    const shift = this.offset + Math.floor(this.counts.length / 2) - middleIndex
    this.shiftCounts(shift)
    this._minIndex = newMin
    this._maxIndex = newMax
  }

  private shiftCounts(shift: number): void {
    if (shift === 0 || this.isEmpty()) return

    const minArr = this._minIndex - this.offset
    const maxArr = this._maxIndex - this.offset

    if (shift > 0) {
      for (let i = maxArr; i >= minArr; i--) {
        this.counts[i + shift] = this.counts[i]
        this.counts[i] = 0
      }
    } else {
      for (let i = minArr; i <= maxArr; i++) {
        this.counts[i + shift] = this.counts[i]
        this.counts[i] = 0
      }
    }
    this.offset -= shift
  }

  merge(other: DenseStore): void {
    if (other.isEmpty()) return

    const otherMin = other.getMinIndex()
    const otherMax = other.getMaxIndex()

    if (otherMin < this._minIndex || otherMax > this._maxIndex) {
      this.extendRange(otherMin, otherMax)
    }

    for (const bin of other.ascendingBins()) {
      this.counts[bin.index - this.offset] += bin.count
    }
  }

  copy(): DenseStore {
    return DenseStore.copyFrom(this)
  }

  clear(): void {
    this.counts.fill(0)
    this._minIndex = Number.MAX_SAFE_INTEGER
    this._maxIndex = Number.MIN_SAFE_INTEGER
    this.offset = 0
  }

  isEmpty(): boolean {
    return this._maxIndex < this._minIndex
  }

  getTotalCount(): number {
    if (this.isEmpty()) return 0
    let total = 0
    for (
      let i = this._minIndex - this.offset;
      i <= this._maxIndex - this.offset;
      i++
    ) {
      total += this.counts[i]
    }
    return total
  }

  getMinIndex(): number {
    if (this.isEmpty()) throw new Error('Store is empty')
    return this._minIndex
  }

  getMaxIndex(): number {
    if (this.isEmpty()) throw new Error('Store is empty')
    return this._maxIndex
  }

  *ascendingBins(): IterableIterator<Bin> {
    if (this.isEmpty()) return
    let index = this._minIndex
    while (index <= this._maxIndex) {
      const count = this.counts[index - this.offset]
      if (count > 0) {
        yield { index, count }
      }
      index++
    }
  }

  *descendingBins(): IterableIterator<Bin> {
    if (this.isEmpty()) return
    let index = this._maxIndex
    while (index >= this._minIndex) {
      const count = this.counts[index - this.offset]
      if (count > 0) {
        yield { index, count }
      }
      index--
    }
  }
}

// ============================================================================
// IndexMapping - Maps values to bucket indices
// ============================================================================

/**
 * Logarithmic index mapping - memory optimal.
 * Maps values using: index = floor(log(value) * multiplier + offset)
 */
export class LogarithmicMapping {
  private readonly gamma: number
  private readonly indexOffset: number
  private readonly multiplier: number
  private readonly _relativeAccuracy: number

  constructor(relativeAccuracy: number, indexOffset = 0) {
    if (relativeAccuracy <= 0 || relativeAccuracy >= 1) {
      throw new Error('Relative accuracy must be between 0 and 1')
    }
    this.gamma = (1 + relativeAccuracy) / (1 - relativeAccuracy)
    this.indexOffset = indexOffset
    this.multiplier = 1 / Math.log1p(this.gamma - 1)
    this._relativeAccuracy = (this.gamma - 1) / (this.gamma + 1)
  }

  index(value: number): number {
    const idx = Math.log(value) * this.multiplier + this.indexOffset
    // Java uses (int)idx which truncates toward zero, then -1 for negatives
    // Math.floor() already rounds toward -infinity, achieving the same result
    return Math.floor(idx)
  }

  value(index: number): number {
    return this.lowerBound(index) * (1 + this._relativeAccuracy)
  }

  lowerBound(index: number): number {
    return Math.exp((index - this.indexOffset) / this.multiplier)
  }

  upperBound(index: number): number {
    return this.lowerBound(index + 1)
  }

  relativeAccuracy(): number {
    return this._relativeAccuracy
  }

  minIndexableValue(): number {
    return Math.max(
      Math.exp(
        (Number.MIN_SAFE_INTEGER - this.indexOffset) / this.multiplier + 1,
      ),
      (Number.MIN_VALUE * (1 + this._relativeAccuracy)) /
        (1 - this._relativeAccuracy),
    )
  }

  maxIndexableValue(): number {
    return Math.min(
      Math.exp(
        (Number.MAX_SAFE_INTEGER - this.indexOffset) / this.multiplier - 1,
      ),
      Number.MAX_VALUE / (1 + this._relativeAccuracy),
    )
  }

  equals(other: LogarithmicMapping): boolean {
    if (!(other instanceof LogarithmicMapping)) return false
    return this.gamma === other.gamma && this.indexOffset === other.indexOffset
  }
}

// ============================================================================
// DDSketch - Main quantile sketch class
// ============================================================================

export interface DDSketchOpts {
  /** Relative accuracy (0 < accuracy < 1). Default: 0.01 (1%) */
  relativeAccuracy?: number
  /** Maximum bins retained across positive and negative stores. */
  maxBins?: number
}

export interface DDSketchState {
  relativeAccuracy: number
  maxBins: number
  zeroCount: number
  positiveBins: Bin[]
  negativeBins: Bin[]
}

/**
 * DDSketch - A quantile sketch with relative-error guarantees.
 *
 * Computes quantile values with an approximation error that is relative
 * to the actual quantile value. Works with positive and negative values.
 *
 * Example: With 1% relative accuracy, if the actual p99 is 100ms,
 * the computed value is guaranteed to be between 99ms and 101ms.
 */
export class DDSketch {
  private readonly indexMapping: LogarithmicMapping
  private readonly relativeAccuracy: number
  private readonly minIndexedValue: number
  private readonly maxIndexedValue: number
  private negativeStore: DenseStore
  private positiveStore: DenseStore
  private readonly maxBins: number
  private zeroCount = 0

  constructor(opts: DDSketchOpts = {}) {
    this.relativeAccuracy = opts.relativeAccuracy ?? 0.01
    this.indexMapping = new LogarithmicMapping(this.relativeAccuracy)
    this.maxBins = opts.maxBins ?? 2048

    this.minIndexedValue = this.indexMapping.minIndexableValue()
    this.maxIndexedValue = this.indexMapping.maxIndexableValue()
    this.negativeStore = new DenseStore()
    this.positiveStore = new DenseStore()
  }

  private static copyFrom(sketch: DDSketch): DDSketch {
    const copy = new DDSketch({
      relativeAccuracy: sketch.relativeAccuracy,
      maxBins: sketch.maxBins,
    })
    // Merge the original stores into the copy's empty stores
    copy.negativeStore.merge(sketch.negativeStore)
    copy.positiveStore.merge(sketch.positiveStore)
    copy.zeroCount = sketch.zeroCount
    return copy
  }

  static fromState(state: DDSketchState): DDSketch {
    const sketch = new DDSketch({
      relativeAccuracy: state.relativeAccuracy,
      maxBins: state.maxBins,
    })
    sketch.zeroCount = state.zeroCount
    for (const bin of state.positiveBins) {
      sketch.positiveStore.add(bin.index, bin.count)
    }
    for (const bin of state.negativeBins) {
      sketch.negativeStore.add(bin.index, bin.count)
    }
    sketch.collapseBins()
    return sketch
  }

  /** Add a value to the sketch */
  add(value: number, count = 1): void {
    if (count < 0) throw new Error('Count cannot be negative')
    if (count === 0) return

    this.checkValueTrackable(value)

    if (value > this.minIndexedValue) {
      this.positiveStore.add(this.indexMapping.index(value), count)
    } else if (value < -this.minIndexedValue) {
      this.negativeStore.add(this.indexMapping.index(-value), count)
    } else {
      this.zeroCount += count
    }
    this.collapseBins()
  }

  private collapseBins(): void {
    const negative = [...this.negativeStore.descendingBins()].map((bin) => ({
      ...bin,
      sign: 'negative' as const,
    }))
    const positive = [...this.positiveStore.ascendingBins()].map((bin) => ({
      ...bin,
      sign: 'positive' as const,
    }))
    const bins = [...negative, ...positive]
    if (bins.length <= this.maxBins) return
    const kept = bins.slice(bins.length - this.maxBins)
    kept[0]!.count += bins
      .slice(0, bins.length - this.maxBins)
      .reduce((sum, bin) => sum + bin.count, 0)
    this.negativeStore = new DenseStore()
    this.positiveStore = new DenseStore()
    for (const bin of kept) {
      const store = bin.sign === 'negative' ? this.negativeStore : this.positiveStore
      store.add(bin.index, bin.count)
    }
  }

  private checkValueTrackable(value: number): void {
    if (value < -this.maxIndexedValue || value > this.maxIndexedValue) {
      throw new Error('Value is outside the trackable range')
    }
  }

  /** Merge another sketch into this one */
  merge(other: DDSketch): void {
    if (!this.indexMapping.equals(other.indexMapping)) {
      throw new Error('Cannot merge sketches with different index mappings')
    }
    this.negativeStore.merge(other.negativeStore)
    this.positiveStore.merge(other.positiveStore)
    this.zeroCount += other.zeroCount
    this.collapseBins()
  }

  /** Create a deep copy of this sketch */
  copy(): DDSketch {
    return DDSketch.copyFrom(this)
  }

  /** Check if sketch is empty */
  isEmpty(): boolean {
    return (
      this.zeroCount === 0 &&
      this.negativeStore.isEmpty() &&
      this.positiveStore.isEmpty()
    )
  }

  /** Clear all data */
  clear(): void {
    this.negativeStore.clear()
    this.positiveStore.clear()
    this.zeroCount = 0
  }

  /** Get total count of values added */
  getCount(): number {
    return (
      this.zeroCount +
      this.negativeStore.getTotalCount() +
      this.positiveStore.getTotalCount()
    )
  }

  /** Get approximate sum of values (accurate if all values have same sign) */
  getSum(): number {
    let sum = 0
    for (const bin of this.negativeStore.ascendingBins()) {
      sum -= this.indexMapping.value(bin.index) * bin.count
    }
    for (const bin of this.positiveStore.ascendingBins()) {
      sum += this.indexMapping.value(bin.index) * bin.count
    }
    return sum
  }

  /** Get approximate average */
  getAverage(): number {
    const count = this.getCount()
    if (count === 0) throw new Error('Sketch is empty')
    return this.getSum() / count
  }

  /** Get approximate minimum value */
  getMin(): number {
    if (!this.negativeStore.isEmpty()) {
      return -this.indexMapping.value(this.negativeStore.getMaxIndex())
    }
    if (this.zeroCount > 0) return 0
    if (!this.positiveStore.isEmpty()) {
      return this.indexMapping.value(this.positiveStore.getMinIndex())
    }
    throw new Error('Sketch is empty')
  }

  /** Get approximate maximum value */
  getMax(): number {
    if (!this.positiveStore.isEmpty()) {
      return this.indexMapping.value(this.positiveStore.getMaxIndex())
    }
    if (this.zeroCount > 0) return 0
    if (!this.negativeStore.isEmpty()) {
      return -this.indexMapping.value(this.negativeStore.getMinIndex())
    }
    throw new Error('Sketch is empty')
  }

  /** Get value at a quantile (0 to 1) */
  getQuantile(quantile: number): number {
    if (quantile < 0 || quantile > 1) {
      throw new Error('Quantile must be between 0 and 1')
    }

    const count = this.getCount()
    if (count === 0) throw new Error('Sketch is empty')

    const rank = quantile * (count - 1)
    let n = 0

    // Iterate negative values (descending index = ascending value from most negative)
    for (const bin of this.negativeStore.descendingBins()) {
      n += bin.count
      if (n > rank) {
        return -this.indexMapping.value(bin.index)
      }
    }

    // Check zero bucket
    n += this.zeroCount
    if (n > rank) return 0

    // Iterate positive values (ascending index = ascending value)
    for (const bin of this.positiveStore.ascendingBins()) {
      n += bin.count
      if (n > rank) {
        return this.indexMapping.value(bin.index)
      }
    }

    throw new Error('Failed to compute quantile')
  }

  /** Get values at multiple quantiles */
  getQuantiles(quantiles: number[]): number[] {
    return quantiles.map((q) => this.getQuantile(q))
  }

  /** Convenience: get common percentiles */
  getPercentiles(): QuantileSummary {
    const [p50, p90, p95, p99] = this.getQuantiles([0.5, 0.9, 0.95, 0.99])
    return { p50, p90, p95, p99 }
  }

  toState(): DDSketchState {
    return {
      relativeAccuracy: this.getRelativeAccuracy(),
      maxBins: this.maxBins,
      zeroCount: this.zeroCount,
      positiveBins: [...this.positiveStore.ascendingBins()],
      negativeBins: [...this.negativeStore.ascendingBins()],
    }
  }

  /** Get the index mapping (for serialization/comparison) */
  getIndexMapping(): LogarithmicMapping {
    return this.indexMapping
  }

  /** Get relative accuracy */
  getRelativeAccuracy(): number {
    return this.indexMapping.relativeAccuracy()
  }
}

// ============================================================================
// Factory functions
// ============================================================================

/**
 * Create a DDSketch with specified relative accuracy.
 * Default: 1% (0.01)
 */
export function createDDSketch(relativeAccuracy = 0.01, maxBins = 2048): DDSketch {
  return new DDSketch({ relativeAccuracy, maxBins })
}

export function createDDSketchFromState(state: DDSketchState): DDSketch {
  return DDSketch.fromState(state)
}

export function mergeDDSketchStates(
  states: DDSketchState[],
): DDSketchState | undefined {
  const [first, ...rest] = states
  if (!first) return undefined
  const sketch = DDSketch.fromState(first)
  for (const state of rest) {
    sketch.merge(DDSketch.fromState(state))
  }
  return sketch.toState()
}

export function percentilesFromDDSketchState(
  state: DDSketchState,
): ReturnType<DDSketch['getPercentiles']> {
  return DDSketch.fromState(state).getPercentiles()
}
